// src/lib/assessmentTemplate.ts
// The shape of an Assessment Module's `fields_config`, and the one pass that
// turns whatever is actually in the database into something a form can render.
//
// Why this exists as its own module rather than living in the screen that
// renders it: Assessment Modules are hospital-authored. An Admin/ED edits
// sections and fields in Admin Panel → Ailment Templates, the result is
// free-form JSONB, and nothing validates it on the way in. The Pre-Consult
// screen must therefore render *whatever* comes back without crashing,
// silently dropping a nurse's answer, or showing a field with no way to
// answer it — and that guarantee is worth stating and testing in one place
// instead of as scattered `|| []` guards at each use.
//
// This is not hypothetical. The due-checklist `items` shape (objects, not
// strings) crashed the Pre-Consult screen once already, and it did so because
// the renderer trusted the template's declared type.
//
// The pass REPAIRS rather than rejects wherever it can: dropping a section
// would lose clinical capture silently, which is the worse failure. It only
// drops what cannot be rendered meaningfully at all (a field with no key, a
// duplicate key, a history table pointing at a kind that has no config).

import type { VisibleIfGroup } from '@/utils/conditions';

/** The three longitudinal tables PatientHistoryPanel knows how to render. */
export type HistoryKind = 'diagnoses' | 'procedures' | 'medications';

/** A due-checklist entry, e.g. `{ label: 'HbA1c', interval_months: 6 }` — see
 * backend/seed_diabetes_module.js. Distinct from the plain `string[]` that
 * checkbox-group/radio/vitals fields use for their options. */
export interface DueChecklistItemDef {
  label: string;
  interval_months?: number;
}

/** A field exactly as authored — loosely typed because `options`/`items`/the
 * stored answer all vary by `type`, and because a hand-edited template can
 * contain anything. Never rendered directly; run it through
 * `normaliseTemplate` first. */
/** One band of a numeric field's scoring scale, e.g. `{max: 7, color: 'green'}`.
 * Bands are ordered ascending and the last one omits `max` as the catch-all —
 * exactly the shape backend/utils/assessmentScoring.js reads. */
export interface ThresholdBand {
  max?: number;
  color: 'green' | 'yellow' | 'red';
}

export interface TemplateField {
  key: string;
  label?: string;
  type: string;
  /** Numeric scoring bands, when the template declares them. */
  thresholds?: unknown;
  /** Deliberately `unknown`: authored shape varies by `type` and a hand-edited
   * template can hold anything. `normaliseTemplate` is what turns these two
   * into the `string[]` / `DueChecklistItemDef[]` the form actually uses. */
  options?: unknown;
  items?: unknown;
  suffix?: string;
  history_source?: HistoryKind;
  visible_if?: VisibleIfGroup;
  /** `choice-matrix` only — see `normaliseMatrixColumns`/`normaliseMatrixRows`
   * and docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §2. `summary_rows` (one
   * summary tile per row vs. one for the whole field) is read directly off
   * the raw JSONB by the backend scorer — nothing here needs it. */
  columns?: unknown;
  rows?: unknown;
  /** Rendered as an info strip under the field — e.g. the diet matrix's
   * pass-level footnote. */
  footnote?: unknown;
  /** `number` only — how often this reading should be repeated, driving
   * NumericField's recency Badge (Pre-Consult redesign Phase 5). Optional:
   * a numeric field with no declared interval can still show "Recent"
   * (under ~3 months) but never "Stale", since there is no interval to be
   * stale against. Same key name/shape as due-checklist items' own
   * `interval_months`, deliberately. */
  interval_months?: unknown;
}

export interface TemplateSection {
  label?: string;
  fields?: TemplateField[];
  /** A hint that this section should be wider in Pre-Consult's `three-up`
   * grid — see docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §4. Anything other
   * than 1 or 2 clamps to 1 at normalise time. */
  layout?: { span?: unknown };
}

export interface AilmentTemplate {
  id: number;
  name: string;
  module_key?: string;
  active: boolean;
  fields_config?: { sections?: TemplateSection[] };
  /** Which of Pre-Consult's four Triage fields this template wants —
   * null/absent means all four. See TriageFieldsEditor.tsx's own type. */
  triage_fields?: string[] | null;
}

/** Field types this renderer knows how to draw. Mirrors FIELD_TYPES in
 * features/admin/ailments/AilmentTemplatesTab.tsx — anything outside this set
 * still renders (as free text), it just isn't drawn specially. */
export const KNOWN_FIELD_TYPES = new Set(['checkbox-group', 'radio', 'text', 'number', 'vitals', 'due-checklist', 'condition-status', 'history-table', 'choice-matrix', 'pictorial-choice']);

const HISTORY_KINDS = new Set<string>(['diagnoses', 'procedures', 'medications']);

/** A `choice-matrix` column, e.g. `{value: 'good', label: 'GOOD (PASS)', color: 'green'}`.
 * See docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §2. */
export interface MatrixColumn {
  value: string;
  label: string;
  color?: 'green' | 'yellow' | 'red';
}

/** One row's answer for a given column — the stored answer is `option` (a
 * human string), never the column's `value`, so a saved assessment reads on
 * its own. `color`, when authored, overrides the column's own colour for
 * this specific row (the field-types spec's inverted-scale example). */
export interface MatrixCell {
  option: string;
  illustration?: string;
  color?: 'green' | 'yellow' | 'red';
}

/** A `choice-matrix` row, e.g. Diet Assessment's "Protein". `cells` is keyed
 * by column `value`; a column with no cell for this row is simply absent —
 * the renderer draws that cell empty and unselectable rather than shifting
 * the grid. */
export interface MatrixRow {
  key: string;
  label: string;
  hint?: string;
  cutoff?: string;
  summary_label?: string;
  cells: Record<string, MatrixCell>;
}

/** A `pictorial-choice` option — single-answer, better recognised than read.
 * Stored answer is `value` (a plain string), identical to `radio` on purpose:
 * a template can switch between the two types with zero data migration. See
 * docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §3. */
export interface PictorialOption {
  value: string;
  label: string;
  sublabel?: string;
  illustration?: string;
  color?: 'green' | 'yellow' | 'red';
}

export interface NormalisedField extends Omit<TemplateField, 'items' | 'options' | 'thresholds' | 'columns' | 'rows' | 'footnote' | 'interval_months'> {
  key: string;
  type: string;
  options: string[];
  items: DueChecklistItemDef[];
  thresholds: ThresholdBand[];
  /** `choice-matrix` only — empty for every other type. */
  columns: MatrixColumn[];
  rows: MatrixRow[];
  footnote?: string;
  /** `number` only — see `TemplateField.interval_months` above. */
  interval_months?: number;
  /** `pictorial-choice` only — empty for every other type. `options` above is
   * still populated (with these same values) for that type too, so voice
   * fill/`describeField` need no special case — see the field-types spec §3. */
  pictorialOptions: PictorialOption[];
  /** False when the authored `type` isn't one this renderer knows. The field
   * still renders, as free text, so the answer is captured rather than the
   * question vanishing. */
  knownType: boolean;
}

export interface NormalisedSection {
  label?: string;
  fields: NormalisedField[];
  /** 1 or 2, always — see `TemplateSection.layout` above. */
  span: 1 | 2;
}

/** A hint, not a guarantee — see the field-types spec §4. Anything other than
 * 1 or 2 (missing, a string, 3, …) clamps to 1 rather than breaking the grid. */
export function normaliseSpan(raw: unknown): 1 | 2 {
  return raw === 2 ? 2 : 1;
}

/** `items`/`options` are authored as plain strings for most field types but as
 * `{label, interval_months}` objects for due-checklist. Accept either
 * everywhere and hand callers exactly one shape. */
export function normaliseItems(raw: unknown): DueChecklistItemDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): DueChecklistItemDef[] => {
    if (typeof item === 'string') return item.trim() ? [{ label: item }] : [];
    if (item && typeof item === 'object') {
      const { label, interval_months: months } = item as { label?: unknown; interval_months?: unknown };
      if (typeof label === 'string' && label.trim()) {
        return [{ label, ...(typeof months === 'number' ? { interval_months: months } : {}) }];
      }
    }
    return [];
  });
}

export function normaliseOptions(raw: unknown): string[] {
  return normaliseItems(raw).map(i => i.label);
}

const BAND_COLORS = new Set(['green', 'yellow', 'red']);

export function normaliseThresholds(raw: unknown): ThresholdBand[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((band): ThresholdBand[] => {
    if (!band || typeof band !== 'object') return [];
    const { max, color } = band as { max?: unknown; color?: unknown };
    if (typeof color !== 'string' || !BAND_COLORS.has(color)) return [];
    return [{ color: color as ThresholdBand['color'], ...(typeof max === 'number' ? { max } : {}) }];
  });
}

/** Turns a label into a stable-ish fallback `value` when a column/row is
 * authored without one — lowercase, non-alphanumerics collapsed to a dash. */
function slugify(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Accepts a bare label list (`["Good", "Partial", "Poor"]` — value :=
 * slugified label, no colour) or the full object form. Drops entries without
 * a usable label — see the field-types spec §2. */
export function normaliseMatrixColumns(raw: unknown): MatrixColumn[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw.flatMap((col): MatrixColumn[] => {
    let label = '';
    let value = '';
    let color: unknown;
    if (typeof col === 'string') {
      label = col.trim();
    } else if (col && typeof col === 'object') {
      const c = col as { value?: unknown; label?: unknown; color?: unknown };
      label = typeof c.label === 'string' ? c.label.trim() : '';
      value = typeof c.value === 'string' ? c.value.trim() : '';
      color = c.color;
    }
    if (!label) return [];
    if (!value) value = slugify(label) || label;
    if (seen.has(value)) return [];
    seen.add(value);
    return [{ value, label, ...(typeof color === 'string' && BAND_COLORS.has(color) ? { color: color as MatrixColumn['color'] } : {}) }];
  });
}

/** `cells` is coerced so every declared column has, at most, one entry — a
 * row missing a cell for a column simply omits that key, which the renderer
 * draws as an empty, unselectable cell rather than reflowing the grid. A
 * duplicate row key drops the later one, the same rule field keys follow. */
export function normaliseMatrixRows(raw: unknown, columns: MatrixColumn[]): MatrixRow[] {
  if (!Array.isArray(raw) || !columns.length) return [];
  const seen = new Set<string>();
  return raw.flatMap((row): MatrixRow[] => {
    if (!row || typeof row !== 'object') return [];
    const r = row as Record<string, unknown>;
    const key = typeof r.key === 'string' ? r.key.trim() : '';
    if (!key || seen.has(key)) return [];
    seen.add(key);
    const label = typeof r.label === 'string' && r.label.trim() ? r.label.trim() : key;
    const rawCells = r.cells && typeof r.cells === 'object' ? r.cells as Record<string, unknown> : {};
    const cells: Record<string, MatrixCell> = {};
    for (const col of columns) {
      const cell = rawCells[col.value];
      if (!cell || typeof cell !== 'object') continue;
      const c = cell as { option?: unknown; illustration?: unknown; color?: unknown };
      if (typeof c.option !== 'string' || !c.option.trim()) continue;
      cells[col.value] = {
        option: c.option.trim(),
        ...(typeof c.illustration === 'string' && c.illustration.trim() ? { illustration: c.illustration.trim() } : {}),
        ...(typeof c.color === 'string' && BAND_COLORS.has(c.color) ? { color: c.color as MatrixCell['color'] } : {}),
      };
    }
    return [{
      key, label,
      ...(typeof r.hint === 'string' && r.hint.trim() ? { hint: r.hint.trim() } : {}),
      ...(typeof r.cutoff === 'string' && r.cutoff.trim() ? { cutoff: r.cutoff.trim() } : {}),
      ...(typeof r.summary_label === 'string' && r.summary_label.trim() ? { summary_label: r.summary_label.trim() } : {}),
      cells,
    }];
  });
}

/** Accepts a bare label list (`normaliseOptions`' own plain-string form — a
 * `pictorial-choice` authored that way renders as a plain card list with no
 * artwork, the correct degradation) or the full object form. A missing
 * `value` defaults to the label itself, matching `radio`'s own convention
 * that the option string IS the stored answer. Drops entries without a
 * usable label; a duplicate value drops the later one. */
export function normalisePictorialOptions(raw: unknown): PictorialOption[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw.flatMap((opt): PictorialOption[] => {
    let label = '';
    let value = '';
    let sublabel: string | undefined;
    let illustration: string | undefined;
    let color: unknown;
    if (typeof opt === 'string') {
      label = opt.trim();
    } else if (opt && typeof opt === 'object') {
      const o = opt as Record<string, unknown>;
      label = typeof o.label === 'string' ? o.label.trim() : '';
      value = typeof o.value === 'string' ? o.value.trim() : '';
      sublabel = typeof o.sublabel === 'string' && o.sublabel.trim() ? o.sublabel.trim() : undefined;
      illustration = typeof o.illustration === 'string' && o.illustration.trim() ? o.illustration.trim() : undefined;
      color = o.color;
    }
    if (!label) return [];
    if (!value) value = label;
    if (seen.has(value)) return [];
    seen.add(value);
    return [{
      value, label,
      ...(sublabel ? { sublabel } : {}),
      ...(illustration ? { illustration } : {}),
      ...(typeof color === 'string' && BAND_COLORS.has(color) ? { color: color as PictorialOption['color'] } : {}),
    }];
  });
}

/** Which band a value falls in. Mirrors assessmentScoring.js's
 * `colorForNumber` exactly — first band whose `max` the value does not exceed,
 * the open-ended band otherwise — so the scale a nurse reads can never
 * disagree with the colour the server scored. -1 when nothing matches. */
export function activeBandIndex(bands: ThresholdBand[], value: unknown): number {
  if (value === null || value === undefined || value === '') return -1;
  const num = Number(value);
  if (!Number.isFinite(num)) return -1;
  return bands.findIndex(b => b.max === undefined || num <= b.max);
}

/** Human labels for each band — "< 7", "7–8.5", "> 8.5" — derived from the
 * bands' own boundaries so a threshold edited in the Admin Panel relabels the
 * scale automatically. `suffix` is the field's unit, e.g. "%". */
export function bandLabels(bands: ThresholdBand[], suffix?: string): string[] {
  const unit = suffix ? suffix.trim() : '';
  const withUnit = (s: string) => (unit ? `${s}${unit.startsWith('%') ? '' : ' '}${unit}` : s);
  return bands.map((band, i) => {
    const lower = i > 0 ? bands[i - 1]!.max : undefined;
    if (band.max === undefined) return withUnit(lower !== undefined ? `> ${lower}` : 'any');
    if (lower === undefined) return withUnit(`< ${band.max}`);
    return withUnit(`${lower}–${band.max}`);
  });
}

export function normaliseTemplate(template: AilmentTemplate | null | undefined): NormalisedSection[] {
  const sections = template?.fields_config?.sections;
  if (!Array.isArray(sections)) return [];
  // Field keys address answers, so a duplicate would make two inputs write to
  // the same slot and appear to overwrite each other. First wins; later
  // duplicates are dropped rather than silently shadowing.
  const seenKeys = new Set<string>();
  return sections.flatMap((section): NormalisedSection[] => {
    if (!section || typeof section !== 'object') return [];
    const rawFields = Array.isArray(section.fields) ? section.fields : [];
    const fields = rawFields.flatMap((field): NormalisedField[] => {
      if (!field || typeof field !== 'object') return [];
      const key = typeof field.key === 'string' ? field.key.trim() : '';
      if (!key || seenKeys.has(key)) return [];
      const type = typeof field.type === 'string' ? field.type : '';
      // A history table pointing at a kind PatientHistoryPanel has no config
      // for would render an empty, unlabelled panel — drop it instead.
      if (type === 'history-table' && !HISTORY_KINDS.has(String(field.history_source))) return [];
      seenKeys.add(key);
      const matrixColumns = type === 'choice-matrix' ? normaliseMatrixColumns(field.columns) : [];
      const matrixRows = type === 'choice-matrix' ? normaliseMatrixRows(field.rows, matrixColumns) : [];
      const pictorialOptions = type === 'pictorial-choice' ? normalisePictorialOptions(field.options) : [];
      return [{
        ...field,
        key,
        type,
        knownType: KNOWN_FIELD_TYPES.has(type),
        // pictorial-choice's options are its own PictorialOption values —
        // reusing this same generic string[] slot (rather than a parallel
        // one) is exactly what lets voice fill/describeField treat it as a
        // plain radio with zero extra code, per the field-types spec §3.
        options: type === 'pictorial-choice' ? pictorialOptions.map(o => o.value) : normaliseOptions(field.options),
        // A vitals row may declare its sub-labels as either; due-checklist
        // only ever means `items`.
        items: normaliseItems(field.items ?? field.options),
        thresholds: normaliseThresholds(field.thresholds),
        columns: matrixColumns,
        rows: matrixRows,
        pictorialOptions,
        footnote: typeof field.footnote === 'string' && field.footnote.trim() ? field.footnote.trim() : undefined,
        interval_months: typeof field.interval_months === 'number' && Number.isFinite(field.interval_months) ? field.interval_months : undefined,
      }];
    });
    return [{
      label: typeof section.label === 'string' ? section.label : undefined,
      fields,
      span: normaliseSpan((section.layout as { span?: unknown } | undefined)?.span),
    }];
  });
}

/** "6 sections · 24 fields" for the template picker, counted off the same
 * normalised shape the form itself renders — so a template whose JSON is
 * partly malformed advertises what will actually appear, not what was
 * authored. */
export function templateShape(template: AilmentTemplate): string {
  const sections = normaliseTemplate(template);
  const fields = sections.reduce((n, s) => n + s.fields.length, 0);
  return `${sections.length} section${sections.length === 1 ? '' : 's'} · ${fields} field${fields === 1 ? '' : 's'}`;
}
